import React, { useEffect, useMemo, useState } from 'react';
import '../css/accounting.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(number);
};

const normalizeStatementStartDay = (value, fallback = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) {
    return fallback;
  }
  return Math.max(1, Math.min(31, parsed));
};

const formatMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const resolveStatementMonthForDate = (date, statementStartDay = 1) => {
  const normalizedStartDay = normalizeStatementStartDay(statementStartDay, 1);
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const effectiveStartDay = Math.min(normalizedStartDay, lastDayOfMonth);

  if (date.getDate() >= effectiveStartDay) {
    return formatMonthKey(new Date(date.getFullYear(), date.getMonth() + 1, 1));
  }

  return formatMonthKey(date);
};

const getCurrentMonthKey = (statementStartDay = 1) => resolveStatementMonthForDate(new Date(), statementStartDay);
const getCurrentYear = () => String(new Date().getFullYear());
const MONTH_ORDER = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const buildMonthKeyForYear = (year, month) => `${year}-${month}`;

const parsePaymentNumber = (value) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) {
    return 0;
  }
  return parsed;
};

const sortMonthKeys = (a, b) => a.localeCompare(b);

const buildStatementRows = (credit) => {
  const charges = Array.isArray(credit.charges) ? credit.charges : [];
  const paidByMonth = credit.paid_by_month || {};

  const chargesByMonth = charges.reduce((accumulator, charge) => {
    const monthKey = charge.statement_month || '';
    if (!monthKey) {
      return accumulator;
    }
    accumulator[monthKey] = (accumulator[monthKey] || 0) + Number(charge.amount || 0);
    return accumulator;
  }, {});

  const months = Array.from(new Set([
    ...Object.keys(chargesByMonth),
    ...Object.keys(paidByMonth)
  ]))
    .filter(Boolean)
    .sort(sortMonthKeys);

  return months.map((monthKey) => {
    const monthSpent = Number(chargesByMonth[monthKey] || 0);
    const monthPaid = Number(paidByMonth[monthKey] || 0);

    return {
      monthKey,
      total: monthSpent,
      paid: monthPaid,
      remaining: monthSpent - monthPaid
    };
  });
};

const formatUtilization = (balance, limit) => {
  const numericLimit = Number(limit || 0);
  if (!numericLimit) {
    return 0;
  }

  return Math.max(0, Math.round((Number(balance || 0) / numericLimit) * 100));
};

function Credits() {
  const [credits, setCredits] = useState([]);
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [name, setName] = useState('');
  const [paymentCreditId, setPaymentCreditId] = useState('');
  const [paymentMonth, setPaymentMonth] = useState(getCurrentMonthKey());
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentYear, setPaymentYear] = useState(getCurrentYear());
  const [yearPaymentsDraft, setYearPaymentsDraft] = useState({});
  const [statusMessage, setStatusMessage] = useState('');
  const [newCreditStatementStartDay, setNewCreditStatementStartDay] = useState('1');
  const [statementStartDayByCredit, setStatementStartDayByCredit] = useState({});

  const user = localStorage.getItem('loggedInUsername');

  const loadCredits = async (token) => {
    const response = await fetch('/api/accounting/credits', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ user })
    });

    if (!response.ok) {
      throw new Error('Failed to load credits');
    }

    return response.json();
  };

  const refreshCredits = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const data = await loadCredits(token);
      const normalized = Array.isArray(data) ? data : [];
      setCredits(normalized);
      const initialStatementDays = {};
      normalized.forEach((credit) => {
        initialStatementDays[String(credit.id)] = String(normalizeStatementStartDay(credit.statement_start_day, 1));
      });
      setStatementStartDayByCredit(initialStatementDays);

      if (!paymentCreditId && normalized.length > 0) {
        const defaultCredit = normalized.find((credit) => String(credit.name || '').trim().toLowerCase() === 'barclaycard') || normalized[0];
        setPaymentCreditId(String(defaultCredit.id));
        setPaymentMonth(getCurrentMonthKey(defaultCredit.statement_start_day));
      }
    } catch (error) {
      setStatusMessage('Unable to load credit balances.');
    }
  };

  useEffect(() => {
    refreshCredits();
  }, []);

  useEffect(() => {
    if (!paymentCreditId || credits.length === 0) {
      return;
    }

    const selectedCredit = credits.find((credit) => String(credit.id) === String(paymentCreditId));
    if (!selectedCredit) {
      return;
    }

    setPaymentMonth(getCurrentMonthKey(selectedCredit.statement_start_day));
  }, [paymentCreditId, credits]);

  const selectedPaymentCredit = useMemo(() => {
    if (!paymentCreditId) {
      return null;
    }
    return credits.find((credit) => String(credit.id) === String(paymentCreditId)) || null;
  }, [paymentCreditId, credits]);

  useEffect(() => {
    if (!selectedPaymentCredit || !paymentYear) {
      setYearPaymentsDraft({});
      return;
    }

    const nextDraft = {};
    MONTH_ORDER.forEach((month) => {
      const monthKey = buildMonthKeyForYear(paymentYear, month);
      const existingValue = Number(selectedPaymentCredit.paid_by_month?.[monthKey] || 0);
      nextDraft[monthKey] = existingValue ? String(existingValue) : '';
    });
    setYearPaymentsDraft(nextDraft);
  }, [selectedPaymentCredit, paymentYear]);

  const handleAddCredit = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/credits/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          name,
          statement_start_day: normalizeStatementStartDay(newCreditStatementStartDay, 1)
        })
      });

      if (response.ok) {
        setName('');
        setNewCreditStatementStartDay('1');
        setIsAddSectionOpen(false);
        setStatusMessage('Credit account added.');
        refreshCredits();
      } else {
        setStatusMessage('Unable to add credit account.');
      }
    } catch (error) {
      setStatusMessage('Unable to add credit account.');
    }
  };

  const handleSavePayment = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/credits/payment/upsert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          credit_id: paymentCreditId,
          statement_month: paymentMonth,
          amount: paymentAmount
        })
      });

      if (response.ok) {
        setPaymentAmount('');
        setStatusMessage('Payment saved.');
        refreshCredits();
      } else {
        setStatusMessage('Unable to save payment.');
      }
    } catch (error) {
      setStatusMessage('Unable to save payment.');
    }
  };

  const handleSaveYearPayments = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    if (!selectedPaymentCredit) {
      setStatusMessage('Select a credit account first.');
      return;
    }

    const normalizedYear = String(paymentYear || '').trim();
    if (!/^\d{4}$/.test(normalizedYear)) {
      setStatusMessage('Enter a valid year (YYYY).');
      return;
    }

    const updates = MONTH_ORDER
      .map((month) => {
        const monthKey = buildMonthKeyForYear(normalizedYear, month);
        const existingAmount = Number(selectedPaymentCredit.paid_by_month?.[monthKey] || 0);
        const targetAmount = parsePaymentNumber(yearPaymentsDraft[monthKey]);
        return {
          monthKey,
          delta: targetAmount - existingAmount
        };
      })
      .filter((item) => Math.abs(item.delta) > 0.000001);

    if (updates.length === 0) {
      setStatusMessage(`No yearly payment changes for ${normalizedYear}.`);
      return;
    }

    try {
      const responses = await Promise.all(
        updates.map((item) => fetch('/api/accounting/credits/payment/upsert', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user,
            credit_id: paymentCreditId,
            statement_month: item.monthKey,
            amount: item.delta
          })
        }))
      );

      const hasFailure = responses.some((response) => !response.ok);
      if (hasFailure) {
        setStatusMessage('Some yearly payment updates failed.');
        return;
      }

      setStatusMessage(`Saved ${updates.length} monthly payment entr${updates.length === 1 ? 'y' : 'ies'} for ${normalizedYear}.`);
      refreshCredits();
    } catch (error) {
      setStatusMessage('Unable to save yearly payments.');
    }
  };

  const handleClearPaymentsForMonth = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    if (!paymentMonth) {
      setStatusMessage('Select a statement month to clear.');
      return;
    }

    if (!window.confirm(`Clear all credit payments for ${paymentMonth}? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch('/api/accounting/credits/payment/clear/month', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          statement_month: paymentMonth
        })
      });

      const data = await response.json();
      if (response.ok) {
        setStatusMessage(`Cleared ${data.deleted_count || 0} payment entr${(data.deleted_count || 0) === 1 ? 'y' : 'ies'} for ${paymentMonth}.`);
        refreshCredits();
      } else {
        setStatusMessage(data?.error || 'Unable to clear payments for month.');
      }
    } catch (error) {
      setStatusMessage('Unable to clear payments for month.');
    }
  };

  const handleSaveStatementPeriod = async (creditId) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return;
    }

    const nextStartDay = normalizeStatementStartDay(statementStartDayByCredit[String(creditId)], 1);

    try {
      const response = await fetch('/api/accounting/credits/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user,
          credit_id: creditId,
          statement_start_day: nextStartDay
        })
      });

      if (response.ok) {
        setStatusMessage('Statement period saved.');
        refreshCredits();
      } else {
        setStatusMessage('Unable to save statement period.');
      }
    } catch (error) {
      setStatusMessage('Unable to save statement period.');
    }
  };

  return (
    <div className="lib-page">
      <section className="accounting-page">
        <div className="section-header">
          <div>
            <h2>Credit Balances</h2>
            <p className="section-subtitle">Track balances across cards and credit lines.</p>
          </div>
          <button className="login-button" type="button" onClick={() => setIsAddSectionOpen((prev) => !prev)}>
            {isAddSectionOpen ? 'Hide Add Account' : 'Show Add Account'}
          </button>
        </div>

        {isAddSectionOpen && (
          <section className="panel">
            <h3>Add Credit Account</h3>
            <div className="form-grid">
              <div className="form-row">
                <label>
                  Name
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label>
                  Statement starts on day
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={newCreditStatementStartDay}
                    onChange={(e) => setNewCreditStatementStartDay(e.target.value)}
                  />
                </label>
                <button className="login-button" type="button" onClick={handleAddCredit}>Add account</button>
              </div>
            </div>
          </section>
        )}

        <section className="panel">
            <h3>Live Balances</h3>
            <p className="panel-sub">Read-only running balance and credit limit for each card.</p>
            {credits.length === 0 ? (
              <div className="empty-state">No credit accounts yet.</div>
            ) : (
              <div className="credit-bubble-grid">
                {credits.map((credit) => {
                  const utilization = formatUtilization(credit.balance, credit.credit_limit);
                  const utilizationFill = Math.min(utilization, 100);

                  return (
                    <article className="credit-bubble-card" key={credit.id}>
                      <div className="credit-bubble-header">
                        <span className="credit-account-name">{credit.name}</span>
                        <div
                          className="credit-usage-circle"
                          style={{ '--usage-percent': `${utilizationFill}%` }}
                        >
                          <span>{utilization}%</span>
                        </div>
                      </div>

                      <div className="credit-bubble-fields">
                        <label>
                          Balance
                          <div className="credit-readonly-value">{formatCurrency(credit.balance)}</div>
                        </label>
                        <label>
                          Credit limit
                          <div className="credit-readonly-value">{formatCurrency(credit.credit_limit)}</div>
                        </label>
                        <label>
                          Statement starts on day
                          <div className="form-row" style={{ marginTop: '0.35rem' }}>
                            <input
                              type="number"
                              min="1"
                              max="31"
                              value={statementStartDayByCredit[String(credit.id)] ?? String(normalizeStatementStartDay(credit.statement_start_day, 1))}
                              onChange={(e) => setStatementStartDayByCredit((previous) => ({
                                ...previous,
                                [String(credit.id)]: e.target.value
                              }))}
                            />
                            <button className="login-button" type="button" onClick={() => handleSaveStatementPeriod(credit.id)}>
                              Save period
                            </button>
                          </div>
                        </label>
                      </div>

                      <div className="credit-bubble-usage">
                        {formatCurrency(credit.balance)} / {formatCurrency(credit.credit_limit)}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

        <section className="panel panel-secondary">
          <h3>Payments by Month</h3>
          <p className="panel-sub">Monthly paid and remaining balance per credit account.</p>
          {credits.length === 0 ? (
            <div className="empty-state">No credit accounts yet.</div>
          ) : (
            <div className="credit-payments-section">
              <div className="payments-grid">
                {credits.map((credit) => {
                  const chargesByMonth = {};
                  (Array.isArray(credit.charges) ? credit.charges : []).forEach((charge) => {
                    const monthKey = charge.statement_month || '';
                    if (!monthKey) {
                      return;
                    }
                    chargesByMonth[monthKey] = (chargesByMonth[monthKey] || 0) + Number(charge.amount || 0);
                  });

                  const paidByMonth = credit.paid_by_month || {};
                  const months = Array.from(new Set([
                    ...Object.keys(chargesByMonth),
                    ...Object.keys(paidByMonth)
                  ]))
                    .filter((monthKey) => monthKey.startsWith(`${paymentYear}-`))
                    .sort(sortMonthKeys);

                  return (
                    <div className="payment-card" key={`payments-${credit.id}`}>
                      <div className="payment-card-title">{credit.name}</div>
                      <div className="payment-months">
                        {months.length > 0 ? (
                          months.map((monthKey) => {
                            const charged = Number(chargesByMonth[monthKey] || 0);
                            const paid = Number(paidByMonth[monthKey] || 0);
                            const remaining = charged - paid;

                            return (
                              <div key={`${credit.id}-${monthKey}`}>
                                <div className="payment-month-row">
                                  <span className="payment-month-name">{monthKey}</span>
                                  <span className="payment-month-amount">{formatCurrency(paid)}</span>
                                </div>
                                <div className="payment-month-remaining">
                                  <span className="payment-remaining-label">Remaining</span>
                                  <span className="payment-remaining-amount">{formatCurrency(remaining)}</span>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="payment-none">No payments recorded for {paymentYear}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {statusMessage && <span className="section-subtitle">{statusMessage}</span>}

        <section className="panel">
          <h3>Statements & Balances</h3>
          <p className="panel-sub">Monthly statement balances by card: total charged, paid, and remaining for each month.</p>
          {credits.length === 0 ? (
            <div className="empty-state">No credit accounts yet.</div>
          ) : (
            <div className="credit-statements-grid">
              {credits.map((credit) => {
                const statementRows = buildStatementRows(credit);

                return (
                  <article className="credit-statement-card" key={`statement-${credit.id}`}>
                    <h4>{credit.name}</h4>

                    {statementRows.length === 0 ? (
                      <div className="empty-state">No statement activity yet.</div>
                    ) : (
                      <div className="table-scroll">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Total</th>
                              <th>Paid</th>
                              <th>Remaining</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statementRows.map((row) => (
                              <tr key={`${credit.id}-${row.monthKey}`}>
                                <td>{row.monthKey}</td>
                                <td>{formatCurrency(row.total)}</td>
                                <td>{formatCurrency(row.paid)}</td>
                                <td>{formatCurrency(row.remaining)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="panel">
          <h3>Add Payment</h3>
          <p className="panel-sub">Record payments by month and card. New entries for the same month are added cumulatively. Use a negative amount to reverse a payment.</p>
          <div className="credit-payment-form">
            <label>
              Credit account
              <select value={paymentCreditId} onChange={(e) => setPaymentCreditId(e.target.value)}>
                <option value="">Select credit account</option>
                {credits.map((credit) => (
                  <option key={credit.id} value={credit.id}>{credit.name}</option>
                ))}
              </select>
            </label>
            <label>
              Statement month
              <input type="month" value={paymentMonth} onChange={(e) => setPaymentMonth(e.target.value)} />
            </label>
            <label>
              Amount paid
              <input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
              />
            </label>
            <button className="login-button" type="button" onClick={handleSavePayment}>Save payment</button>
            <button className="login-button danger-button" type="button" onClick={handleClearPaymentsForMonth}>Clear month payments</button>
          </div>
          <div className="credit-payment-form" style={{ marginTop: '1rem' }}>
            <label>
              Payment year
              <input
                type="number"
                min="2000"
                max="2100"
                value={paymentYear}
                onChange={(e) => setPaymentYear(e.target.value)}
              />
            </label>
          </div>

          {selectedPaymentCredit && (
            <div className="table-scroll" style={{ marginTop: '0.75rem' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Payment amount</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTH_ORDER.map((month) => {
                    const monthKey = buildMonthKeyForYear(paymentYear || getCurrentYear(), month);
                    return (
                      <tr key={monthKey}>
                        <td>{monthKey}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={yearPaymentsDraft[monthKey] ?? ''}
                            onChange={(e) => setYearPaymentsDraft((previous) => ({
                              ...previous,
                              [monthKey]: e.target.value
                            }))}
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="credit-payment-form" style={{ marginTop: '0.75rem' }}>
            <button className="login-button" type="button" onClick={handleSaveYearPayments} disabled={!selectedPaymentCredit}>
              Save year payments
            </button>
          </div>
        </section>
      </section>
    </div>
  );
}

export default Credits;
